import React from 'react';
import { Controller, type Control } from 'react-hook-form';
import { IconRefresh } from '@tabler/icons-react';
import { Button } from '@/admin/components/ui/button';
import { Input } from '@/admin/components/ui/input';
import { Label } from '@/admin/components/ui/label';
import { Textarea } from '@/admin/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/admin/components/ui/select';
import { CollapsibleSection } from './collapsible-section';
import type { User } from '@/admin/lib/api';
import type { SkillFormData } from '@/admin/pages/skill-detail';

interface BasicInfoSectionProps {
  control: Control<SkillFormData>;
  users: User[];
  loadingUsers: boolean;
  syncing: boolean;
  onSync: () => Promise<void>;
  skillName: string;
  skillDescription: string | undefined;
}

export const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  control,
  users,
  loadingUsers,
  syncing,
  onSync,
  skillName,
  skillDescription,
}) => {
  return (
    <CollapsibleSection
      id="basic-info"
      title="Basic Information"
      description="Name, description, and metadata"
      defaultOpen={true}
      headerActions={
        <Button
          variant="ghost"
          size="sm"
          className="h-6 text-xs px-2"
          onClick={e => {
            e.stopPropagation();
            onSync();
          }}
          disabled={syncing}
        >
          <IconRefresh
            className={`h-3 w-3 mr-1 ${syncing ? 'animate-spin' : ''}`}
          />
          Sync from SKILL.md
        </Button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left Column */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-xs">
              Name
              <span className="text-muted-foreground ml-1">(from SKILL.md)</span>
            </Label>
            <Input
              id="name"
              value={skillName}
              disabled
              className="h-9 bg-muted/50"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="display_name" className="text-xs">
              Display Name
              <span className="text-muted-foreground ml-1">(for public pages)</span>
            </Label>
            <Controller
              name="display_name"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="display_name"
                  placeholder={skillName || 'Enter display name'}
                  className="h-9"
                />
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="version" className="text-xs">
                Version
              </Label>
              <Controller
                name="version"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="version"
                    placeholder="1.0.0"
                    className="h-9"
                  />
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category" className="text-xs">
                Category
              </Label>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="category"
                    placeholder="Research"
                    className="h-9"
                  />
                )}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="author" className="text-xs">
              Author
            </Label>
            <Controller
              name="author_id"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="author" className="h-9">
                    <SelectValue
                      placeholder={loadingUsers ? 'Loading...' : 'Select author'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map(user => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.display_name || user.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="description" className="text-xs">
              Description
              <span className="text-muted-foreground ml-1">(from SKILL.md)</span>
            </Label>
            <Textarea
              id="description"
              value={skillDescription || ''}
              disabled
              rows={6}
              className="text-sm resize-none bg-muted/50"
            />
            <p className="text-[10px] text-muted-foreground">
              Edit SKILL.md to update. Used as default for SEO description.
            </p>
          </div>
        </div>
      </div>
    </CollapsibleSection>
  );
};
